/**
 * Records a version of the 209 whenever one of its boxes saves.
 *
 * The card's boxes all write through `useUpsertIncidentStatusSummary`, and each
 * write should leave a draft version behind the way the 202's boxes do. Rather
 * than thread a callback through all fourteen sections, the card provides the
 * recorder here and the upsert hook picks it up.
 *
 * The default is a no-op so the hook keeps working outside the card, where
 * there is no operational period to record against.
 */
import React, { createContext, useContext, useMemo } from 'react';

import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

const RecordStatusSummaryVersionContext = createContext<() => void>(() => {});

export const useRecordStatusSummaryVersion = (): (() => void) =>
  useContext(RecordStatusSummaryVersionContext);

export const StatusSummaryVersionRecording: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const recordVersion = useRecordFormVersionSave(
    'ICS_209',
    effectivePeriodNumber
  );

  const record = useMemo(
    () => () => {
      if (!readOnlyForms) recordVersion.mutate();
    },
    [readOnlyForms, recordVersion]
  );

  return (
    <RecordStatusSummaryVersionContext.Provider value={record}>
      {children}
    </RecordStatusSummaryVersionContext.Provider>
  );
};

export default StatusSummaryVersionRecording;
